import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import TeamSetup from './components/Auth/TeamSetup';
import Dashboard from './components/Dashboard/Dashboard';
import HappinessHistory from './components/Dashboard/HappinessHistory';
import ElementHistory from './components/Dashboard/ElementHistory';
import TeamManagement from './components/Team/TeamManagement';
import TeamFeedbackPage from './components/Team/Feedback/TeamFeedbackPage';
import Evaluation from './components/Evaluation/Evaluation';
import Handbook from './components/Handbook/Handbook';
import RecommendationDetail from './components/Handbook/RecommendationDetail';
import Profile from './components/User/Profile';
import ReleaseNotes from './components/ReleaseNotes/ReleaseNotes';
import Feedback from './components/Feedback/Feedback';
import Network from './components/Network/Network';
import PublicRecommendations from './components/Public/PublicRecommendations';
import PublicElements from './components/Public/PublicElements';
import PublicConnections from './components/Public/PublicConnections';
import LandingPage from './components/Public/LandingPage';
import Header from './components/Layout/Header';
import Navigation from './components/Layout/Navigation';
import OnboardingTour from './components/Onboarding/OnboardingTour';
import './App.css';

const AuthRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const [isLogin, setIsLogin] = useState(true);

  if (loading) return null;

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return isLogin ? (
    <Login onSwitchToRegister={() => setIsLogin(false)} />
  ) : (
    <Register onSwitchToLogin={() => setIsLogin(true)} />
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const { user } = useAuth();

  if (!user?.hasTeam) {
    return <Navigate to="/team-setup" replace />;
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Navigation />
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-[95%] max-w-7xl mx-auto h-full p-6">
            <OnboardingTour />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/public/landing" element={<LandingPage />} />
          <Route path="/public/recommendations" element={<PublicRecommendations />} />
          <Route path="/public/elements" element={<PublicElements />} />
          <Route path="/public/connections" element={<PublicConnections />} />

          <Route path="/" element={<AuthRoute />} />

          <Route path="/team-setup" element={
            <ProtectedRoute>
              <TeamSetup />
            </ProtectedRoute>
          } />

          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/happiness-history" element={
            <ProtectedRoute>
              <AppLayout>
                <HappinessHistory />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/element-history" element={
            <ProtectedRoute>
              <AppLayout>
                <ElementHistory />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/network" element={
            <ProtectedRoute>
              <AppLayout>
                <Network />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/evaluations" element={
            <ProtectedRoute>
              <AppLayout>
                <Evaluation />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/handbook" element={
            <ProtectedRoute>
              <AppLayout>
                <Handbook />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/handbook/recommendation/:id" element={
            <ProtectedRoute>
              <AppLayout>
                <RecommendationDetail />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/team-feedback" element={
            <ProtectedRoute>
              <AppLayout>
                <TeamFeedbackPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/team-management" element={
            <ProtectedRoute>
              <AppLayout>
                <TeamManagement />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/release-notes" element={
            <ProtectedRoute>
              <AppLayout>
                <ReleaseNotes />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/feedback" element={
            <ProtectedRoute>
              <AppLayout>
                <Feedback />
              </AppLayout>
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;